import type { CSSProperties, HTMLAttributes, ReactNode, Ref } from "react"
import { Link } from "@inertiajs/react"
import { ChannelIcon } from "@/lib/channel-icons"
import { channelColor } from "@/lib/channel-colors"
import { CardMedia } from "@/components/attachments/CardMedia"
import { TagBadge, type Tag as TagOption } from "@/components/tags/TagBadge"
import { cn } from "@/lib/utils"

// The one compact piece-card design shared by the calendar (week/month/list)
// and the pipeline (board/list). Collapsed, every card is the same standard
// height: line 1 carries the channel icon + a short meta string (posting
// time, or channel name when unscheduled) with small indicators on the
// right; lines 2–3 are the piece's text clamped to two lines.
//
// Hovering expands the card. `expand: "overlay"` floats an expanded card in
// place above the surrounding grid — wider and taller, shifting nothing
// (calendar week/month, pipeline board). `expand: "flow"` swaps the
// collapsed card for the expanded one in-flow — same width, taller, pushing
// the rows below down (calendar list, pipeline list). Expanded, the card
// shows the full text (scrolling past 8 lines), a media icon button when
// the piece has an image/video, any caller extras, and the piece's tags.
export type CompactCardMediaProps = {
  imageUrl?: string | null
  videoUrl?: string | null
  videoFilename?: string | null
}

type Props = {
  href: string
  onClick?: (e: React.MouseEvent) => void
  channelType: string | null
  channelColorName: string | null | undefined
  // Line-1 text next to the channel icon.
  meta: string
  // Small line-1 indicators (badges, past-due triangle) — shown collapsed
  // and expanded, kept to line height so they never grow the card.
  headerRight?: ReactNode
  // Line-1 extras only shown in the expanded state (e.g. an action menu).
  expandedHeaderRight?: ReactNode
  text: string
  media?: CompactCardMediaProps
  tags?: TagOption[]
  // Rendered in the expanded state between the media icon and the tags.
  expandedExtras?: ReactNode
  expand: "overlay" | "flow"
  // Overlay growth direction — "right" anchors the overlay's right edge so
  // it grows leftward (for cards in the rightmost columns).
  expandAlign?: "left" | "right"
  // Dashed, faded display-only styling (calendar upload ghosts).
  ghost?: boolean
  // Extra border/ring classes layered onto both states.
  chromeExtra?: string
  borderStrong?: boolean
  shadow?: boolean
  grabCursor?: boolean
  // Suppresses the hover expansion entirely (e.g. mid-drag).
  hoverDisabled?: boolean
  // Keeps the expanded state visible without hover (e.g. while a dropdown
  // rendered from it is open in a portal).
  forceExpanded?: boolean
  as?: "div" | "li"
  wrapperRef?: Ref<never>
  wrapperStyle?: CSSProperties
  wrapperProps?: HTMLAttributes<HTMLElement>
  // Rendered inside the wrapper, outside both card states (portals, dialogs).
  children?: ReactNode
}

export function CompactPieceCard({
  href,
  onClick,
  channelType,
  channelColorName,
  meta,
  headerRight,
  expandedHeaderRight,
  text,
  media,
  tags,
  expandedExtras,
  expand,
  expandAlign = "left",
  ghost = false,
  chromeExtra,
  borderStrong = false,
  shadow = false,
  grabCursor = false,
  hoverDisabled = false,
  forceExpanded = false,
  as: Tag = "div",
  wrapperRef,
  wrapperStyle,
  wrapperProps,
  children,
}: Props) {
  const color = channelColor(channelColorName)
  const hasMedia = Boolean(media?.imageUrl || media?.videoUrl)

  const chrome = cn(
    "rounded-md border",
    ghost
      ? cn("border-2 border-dashed", color.cardBorder)
      : borderStrong
        ? color.borderStrong
        : color.cardBorder,
    chromeExtra
  )

  const headerRow = (extra?: ReactNode) => (
    <div className="flex items-center gap-1.5">
      <ChannelIcon channelType={channelType} color={channelColorName ?? undefined} className="h-3 w-3 shrink-0" />
      <span className="min-w-0 flex-1 truncate text-[10px] font-medium text-ink-muted">{meta}</span>
      {headerRight}
      {extra}
    </div>
  )

  const collapsedCard = (
    <Link
      href={href}
      onClick={onClick}
      className={cn(
        "block select-none p-2 text-xs no-underline",
        chrome,
        ghost
          ? "cursor-pointer bg-page/40 opacity-60"
          : cn(grabCursor ? "cursor-grab" : "cursor-pointer", color.cardBg),
        shadow && "shadow-lg",
        // Flow mode swaps the collapsed card out while the expanded one is
        // in; never while the hover expansion is suppressed.
        expand === "flow" && !hoverDisabled && "group-hover:hidden",
        expand === "flow" && !hoverDisabled && forceExpanded && "hidden"
      )}
    >
      {headerRow()}
      {/* Fixed two-line text box so every card lands at the same height,
          whether the piece text fills one line or ten. */}
      <p className={cn("mt-1 h-8 overflow-hidden text-xs font-medium leading-4 break-words line-clamp-2", color.text)}>
        {text}
      </p>
    </Link>
  )

  const expandedContent = (
    <>
      {headerRow(expandedHeaderRight)}
      <div className="mt-1 max-h-32 overflow-y-auto overscroll-contain">
        <Link
          href={href}
          onClick={onClick}
          className={cn("block whitespace-pre-wrap break-words font-medium no-underline leading-4", color.text)}
        >
          {text}
        </Link>
      </div>
      {hasMedia && (
        <CardMedia
          imageUrl={media?.imageUrl}
          videoUrl={media?.videoUrl}
          videoFilename={media?.videoFilename}
          layout="icon"
        />
      )}
      {expandedExtras}
      {tags && tags.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {tags.map((tag) => (
            <TagBadge key={tag.id} tag={tag} className="px-1.5 py-0.5 text-[10px]" />
          ))}
        </div>
      )}
    </>
  )

  return (
    <Tag
      ref={wrapperRef as Ref<never>}
      style={wrapperStyle}
      className="group relative"
      {...wrapperProps}
    >
      {collapsedCard}

      {!hoverDisabled &&
        (expand === "flow" ? (
          // In-flow: same width, taller, pushes the rows below down.
          <div
            className={cn(
              "hidden p-2 text-xs group-hover:block",
              forceExpanded && "block",
              chrome,
              ghost ? "bg-page/70" : color.cardBg
            )}
          >
            {expandedContent}
          </div>
        ) : (
          // Overlay: floats above the surrounding grid without shifting any
          // cells, growing right/down (leftward when right-aligned). The
          // opaque bg-surface underlay keeps the (translucent in dark mode)
          // channel tint from letting covered cards show through.
          <div
            className={cn(
              "invisible absolute top-0 z-40 opacity-0",
              "transition-[opacity,visibility] delay-150 duration-100",
              "group-hover:visible group-hover:opacity-100",
              forceExpanded && "visible opacity-100 delay-0",
              expandAlign === "right" ? "right-0" : "left-0",
              "w-[150%] min-w-60 max-w-80 rounded-md bg-surface shadow-lg"
            )}
          >
            <div className={cn("p-2 text-xs", chrome, ghost ? "bg-page/70" : color.cardBg)}>
              {expandedContent}
            </div>
          </div>
        ))}
      {children}
    </Tag>
  )
}
